import { Controller } from '@hotwired/stimulus';
import { fairEnd, formatFairRange, nextFairStart } from '../../gillamoos/fair-dates.js';

/**
 * Ticks every second toward the next Gillamoos start.
 *
 * Three states:
 *  - countdown:  now < start         → show days/hours/minutes/seconds
 *  - running:    start <= now < end  → swap to the celebratory block
 *  - elapsed:    now >= end          → roll to next year's dates in place
 *
 * Der Elapsed-Fall hat früher die Seite neu geladen, damit PHP neu rechnet.
 * Das war eine Endlosschleife: die Seite kommt aus dem Pimcore-Full-Page-Cache,
 * der Reload liefert dieselben abgelaufenen ISO-Werte, also sofort der nächste
 * Reload — die ganze Seite sprang im Sekundentakt.
 */
export default class extends Controller {
    static targets = ['days', 'hours', 'minutes', 'seconds', 'running', 'dates', 'year'];
    static values = {
        start: String,
        end: String,
    };

    connect() {
        this.start = this.parseIso(this.startValue);
        this.end = this.parseIso(this.endValue);

        if (!this.start || !this.end) return;

        this.tick();
        this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnect() {
        if (this.interval) {
            window.clearInterval(this.interval);
        }
    }

    tick() {
        const now = new Date();

        if (now >= this.end) {
            this.rollOver(now);
        }

        if (now >= this.start) {
            this.element.classList.add('is-running');
            return;
        }

        this.element.classList.remove('is-running');

        let delta = Math.max(0, Math.floor((this.start.getTime() - now.getTime()) / 1000));
        const days = Math.floor(delta / 86400); delta -= days * 86400;
        const hours = Math.floor(delta / 3600); delta -= hours * 3600;
        const minutes = Math.floor(delta / 60); delta -= minutes * 60;
        const seconds = delta;

        this.write(this.daysTarget, days);
        this.write(this.hoursTarget, hours);
        this.write(this.minutesTarget, minutes);
        this.write(this.secondsTarget, seconds);
    }

    /** Serverwerte sind abgelaufen (meist: gecachtes HTML) → nächstes Jahr lokal setzen. */
    rollOver(now) {
        this.start = nextFairStart(now);
        this.end = fairEnd(this.start);

        if (this.hasDatesTarget) {
            this.datesTarget.textContent = formatFairRange(this.start);
        }
        if (this.hasYearTarget) {
            this.yearTarget.textContent = String(this.start.getFullYear());
        }
    }

    write(target, value) {
        if (!target) return;
        const formatted = String(value).padStart(2, '0');
        if (target.textContent !== formatted) {
            target.textContent = formatted;
        }
    }

    parseIso(value) {
        if (!value) return null;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }
}
